import { log, LoggingType } from '@/logging/CustomLogging';
import ApiCommandHandler from './ApiCommandHandler';
import IApiCommand from './IApiCommand';
import IPersistenceStatusHandler from './IPersistenceStatusHandler';
import PersistenceStatus from './PersistenceStatus';

class RetryManager {
  private static readonly MAXIMUM_RETRY_COUNT = 4;
  private static readonly WAIT_VALUE = 4000;
  private readonly apiCommandHandler: ApiCommandHandler = new ApiCommandHandler();
  private _persistenceStatusHandler: IPersistenceStatusHandler | undefined;

  set persistenceStatusHandler(handler: IPersistenceStatusHandler) {
    this._persistenceStatusHandler = handler;
  }

  public async runCommand<T = never>(command: IApiCommand<T>): Promise<T | null> {
    return this.handleCommand(command, RetryManager.MAXIMUM_RETRY_COUNT);
  }

  private async handleCommand<T = never>(command: IApiCommand<T>, numberRetries: number): Promise<T | null> {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;
    return new Promise<T | null>((resolve, reject) => {
      if (numberRetries < 0) {
        that.notifyPersistenceError(PersistenceStatus.UNRECOVERABLE_ERROR);

        return reject(new Error('Reached maximum number of retries'));
      } else {
        setTimeout(function () {
          that.apiCommandHandler
            .runCommand(command)
            .then((data) => resolve(data))
            .catch((error) => {
              that.notifyPersistenceError(PersistenceStatus.INITIAL_ERROR);
              log('RetryManager: ' + error, LoggingType.ERROR);
              return that
                .handleCommand(command, --numberRetries)
                .then((data) => {
                  that.notifyPersistenceError(PersistenceStatus.NO_ERROR);
                  resolve(data);
                })
                .catch((error) => {
                  reject(error);
                });
            });
        }, (RetryManager.MAXIMUM_RETRY_COUNT - numberRetries) * RetryManager.WAIT_VALUE);
      }
    });
  }

  private notifyPersistenceError(value: PersistenceStatus): void {
    if (this._persistenceStatusHandler) {
      this._persistenceStatusHandler.handlePersistenceError(value);
    }
  }
}

export default RetryManager;
